import React, { createContext, useContext, useRef, useCallback, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { CONFIG_VARS } from "../constants/configuration";

const RecaptchaContext = createContext();

const RECAPTCHA_SITE_KEY = CONFIG_VARS.recaptchaSiteKey;
const RECAPTCHA_READY_TIMEOUT_MS = 10000;

export const RecaptchaProvider = ({ children }) => {
  const recaptchaRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  const waitForReady = useCallback(async () => {
    if (isReady && recaptchaRef.current) {
      return;
    }

    const startedAt = Date.now();
    while (Date.now() - startedAt < RECAPTCHA_READY_TIMEOUT_MS) {
      if (recaptchaRef.current && window.grecaptcha?.execute) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    throw new Error("reCAPTCHA is not initialized");
  }, [isReady]);

  const executeRecaptcha = useCallback(async () => {
    await waitForReady();

    if (!recaptchaRef.current) {
      throw new Error("reCAPTCHA is not initialized");
    }

    try {
      // For invisible reCAPTCHA, execute it programmatically
      const token = await recaptchaRef.current.executeAsync();

      if (!token) {
        throw new Error("Failed to get reCAPTCHA token");
      }

      return token;
    } catch (error) {
      console.error("reCAPTCHA execution failed:", error);
      throw new Error("reCAPTCHA verification failed");
    }
  }, [waitForReady]);

  const resetRecaptcha = useCallback(() => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  }, []);

  const value = {
    executeRecaptcha,
    resetRecaptcha,
    recaptchaRef
  };

  return (
    <RecaptchaContext.Provider value={value}>
      {children}
      {/* Invisible reCAPTCHA component */}
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={RECAPTCHA_SITE_KEY}
        size="invisible"
        badge="bottomleft"
        asyncScriptOnLoad={() => setIsReady(true)}
      />
    </RecaptchaContext.Provider>
  );
};

export const useRecaptcha = () => {
  const context = useContext(RecaptchaContext);

  if (!context) {
    throw new Error("useRecaptcha must be used within a RecaptchaProvider");
  }

  return context;
};
